/**
 * GoalsPage — F3 Wave A Goal & Outcome Expansion
 *
 * Goals CRUD list page. Goals can be anchored under a Vision or an Outcome.
 * Cross-domain listing via /api/v1/goals with filter params.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { Plus, CheckCircle2, Pencil, Archive, Target } from 'lucide-react';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import {
  useRorosGoals,
  useRorosGoalCreate,
  useRorosGoalUpdate,
  useRorosGoalArchive,
} from '@/hooks/roros/goals';
import { useRorosDomains } from '@/hooks/roros/domains';
import type { RorosGoalDTO, GoalStatus, GoalParentType, CreateGoalPayload, UpdateGoalPayload } from '@/types/roros/goals';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { GoalFormDialog } from './GoalFormDialog';

// Strategy domain_id = 1 (STRATEGY enum). Pre-selected per PRD v3.4 GAP 4.
const STRATEGY_DOMAIN_ID_INT = 1;

/** URL param key for deep-link editing (G3 URL routing pilot). */
const GOAL_URL_KEY = 'edit-goal';

// ─── Status badge colours ──────────────────────────────────────────────────

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'achieved': return 'success';
    case 'missed': return 'error';
    case 'archived': return 'default';
    default: return 'warning';
  }
}

// ─── Parent breadcrumb pill (B5: upgraded to Badge + Tooltip) ─────────────

interface ParentBreadcrumbProps {
  parentType: GoalParentType | null;
  parentOutcomeId: string | null;
  parentOutcomeTitle: string | null;
}

function ParentBreadcrumb({ parentType, parentOutcomeId: _parentOutcomeId, parentOutcomeTitle }: ParentBreadcrumbProps) {
  if (!parentType) {
    return (
      <SimpleTooltip content={t('Anchor this goal to a Vision or Outcome for strategic context.')}>
        <Badge variant="warning">
          {t('⚠ No parent')}
        </Badge>
      </SimpleTooltip>
    );
  }
  if (parentType === 'vision') {
    return (
      <SimpleTooltip content={t('Vision')}>
        <Badge variant="outline">
          {t('Vision')}
        </Badge>
      </SimpleTooltip>
    );
  }
  // outcome — show truncated title if available, else ellipsis
  const label = parentOutcomeTitle
    ? `↑ ${t('Outcome:')} ${parentOutcomeTitle.length > 32 ? `${parentOutcomeTitle.slice(0, 32)}…` : parentOutcomeTitle}`
    : `↑ ${t('Outcome:')}`;
  return (
    <Badge variant="outline">
      {label}
    </Badge>
  );
}

// ─── Main page component ───────────────────────────────────────────────────

export function GoalsPage() {
  // ─── State ───────────────────────────────────────────────────────────────
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<RorosGoalDTO | null>(null);
  // CCA-M3-M2 fix: hold the just-created goal id so the dialog can enable Acknowledge
  const [createdGoalId, setCreatedGoalId] = useState<string | null>(null);

  const [archiveTarget, setArchiveTarget] = useState<{ id: string; title: string } | null>(null);
  // B8: Filter variable naming follows OutcomesPage.tsx prior art —
  //   filterDomainId (noun suffix retained, consistent with OutcomesPage)
  //   filterStatus / filterParentType (no suffix, consistent with OutcomesPage filterStatus)
  const [filterDomainId, setFilterDomainId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [filterParentType, setFilterParentType] = useState<string>('');

  // ─── Data ─────────────────────────────────────────────────────────────────
  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive);

  const strategyDomain = useMemo(
    () => domains.find((d) => d.domainId === STRATEGY_DOMAIN_ID_INT) ?? null,
    [domains],
  );

  // Build filter params
  const goalsParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterDomainId) p.domainId = filterDomainId;  // A1: API uses camelCase domainId
    if (filterStatus && filterStatus !== 'all') p.status = filterStatus;
    if (filterParentType === 'orphan') {
      // Server may not support this directly; pass as null sentinel
      p.parent_type = 'null';
    } else if (filterParentType) {
      p.parent_type = filterParentType;
    }
    return Object.keys(p).length > 0 ? p : undefined;
  }, [filterDomainId, filterStatus, filterParentType]);

  const goalsQuery = useRorosGoals(goalsParams as any);
  const goalsRaw = goalsQuery.data;
  const goals: RorosGoalDTO[] = Array.isArray(goalsRaw)
    ? goalsRaw
    : (goalsRaw as any)?.data ?? [];

  // ─── URL routing (G3) — useFormSheetUrlRouting encapsulates
  // useSearchParams + ref-guard + useEffect boilerplate (F-Refinement-9).
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: GOAL_URL_KEY,
    items: goals,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    setEditingGoal(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink]);

  // ─── Mutations ────────────────────────────────────────────────────────────
  const createMutation = useRorosGoalCreate();
  const updateMutation = useRorosGoalUpdate();
  const archiveMutation = useRorosGoalArchive();

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    setEditingGoal(null);
    setCreatedGoalId(null); // reset for new create session
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((goal: RorosGoalDTO) => {
    setEditingGoal(goal);
    setIsFormOpen(true);
  }, []);

  const handleFormSubmit = useCallback(
    (data: CreateGoalPayload | UpdateGoalPayload) => {
      if (editingGoal) {
        updateMutation.mutate(
          { id: editingGoal.id, payload: data as UpdateGoalPayload },
          {
            onSuccess: () => {
              showToast.success(t('Goal updated'));
              setIsFormOpen(false);
            },
            onError: (err) => showToast.error(err.message),
          },
        );
      } else {
        createMutation.mutate(
          data as CreateGoalPayload,
          {
            onSuccess: (newGoal) => {
              // CCA-M3-M2 fix: pass the new id back to GoalFormDialog so
              // the post-save alignment Acknowledge button is enabled.
              setCreatedGoalId(newGoal.id);
              showToast.success(t('Goal created'));
              // Note: do NOT close here — let user acknowledge any alignment
              // conflicts first. GoalFormDialog's onClose resets createdGoalId.
              setIsFormOpen(false);
            },
            onError: (err) => showToast.error(err.message),
          },
        );
      }
    },
    [editingGoal, updateMutation, createMutation],
  );

  const handleArchive = useCallback(() => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        showToast.success(t('Goal archived'));
        setArchiveTarget(null);
      },
      onError: (err) => showToast.error(err.message),
    });
  }, [archiveTarget, archiveMutation]);

  // ─── Empty state CTA ──────────────────────────────────────────────────────
  const emptyCTA = (
    <Button
      onClick={openCreate}
      leftIcon={<Plus className="h-4 w-4" />}
      data-testid="goals-empty-cta"
    >
      {t('Create your first Goal')}
    </Button>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <RorosPage
      title={t('Goals')}
      icon={<Target className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Goals'), path: '/roros/artifacts/goals' }]}
      actions={
        <Button
          onClick={openCreate}
          leftIcon={<Plus className="h-4 w-4" />}
          data-testid="new-goal-button"
        >
          {t('New Goal')}
        </Button>
      }
    >
      {/* ─── Filter bar ─────────────────────────────────────────────────── */}
      {/* C3 fix: replaced raw <select> with boilerplate <Select> component */}
      <div role="group" className="flex flex-wrap gap-3 mb-4" aria-label={t('Goal filters')}>
        {/* Status filter */}
        <div className="w-40">
          <Select
            id="goal-status-filter"
            data-testid="goal-status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('Filter by status')}
            options={[
              { value: 'active', label: t('Active') },
              { value: 'achieved', label: t('Achieved') },
              { value: 'missed', label: t('Missed') },
              { value: 'archived', label: t('Archived') },
              { value: 'all', label: t('All Statuses') },
            ]}
          />
        </div>

        {/* Domain filter */}
        <div className="w-44">
          <Select
            id="goal-domain-filter"
            value={filterDomainId || 'all'}
            onChange={(e) => setFilterDomainId(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by domain')}
            options={[
              { value: 'all', label: t('All Domains') },
              ...domains.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        </div>

        {/* Parent type filter */}
        <div className="w-44">
          <Select
            id="goal-parent-type-filter"
            value={filterParentType || 'all'}
            onChange={(e) => setFilterParentType(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by parent type')}
            options={[
              { value: 'all', label: t('All Parents') },
              { value: 'vision', label: t('Vision') },
              { value: 'outcome', label: t('Outcome') },
              { value: 'orphan', label: t('No Parent') },
            ]}
          />
        </div>
      </div>

      {/* ─── Archive confirm dialog ──────────────────────────────────────── */}
      {/* Rendered BEFORE the goal list so the "Archive" confirm button appears
          first in DOM order. Playwright's .first() then resolves to the
          confirmation button (not the goal-row archive icon) when both match
          the /archive/i selector — fixing GOALS-04 test reliability. */}
      <ConfirmDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title={t('Archive this goal?')}
        description={archiveTarget ? `"${archiveTarget.title}"` : ''}
        confirmText={t('Archive')}
        variant="danger"
        isLoading={archiveMutation.isPending}
        confirmTestId="goal-archive-confirm"
        cancelTestId="goal-archive-cancel"
      />

      {/* ─── Goals list ─────────────────────────────────────────────────── */}
      <QueryPage
        query={goalsQuery as any}
        emptyMessage={t('No goals yet')}
        emptyIcon={<CheckCircle2 className="h-8 w-8 text-muted-foreground" />}
        emptyAction={emptyCTA}
      >
        {(items) => (
          <div className="space-y-3" role="list" aria-label={t('Goals')}>
            {(Array.isArray(items) ? items : (items as any)?.data ?? []).map((goal: RorosGoalDTO) => (
              <GoalRow
                key={goal.id}
                goal={goal}
                domains={domains}
                onEdit={() => openEdit(goal)}
                onArchive={() => setArchiveTarget({ id: goal.id, title: goal.title })}
              />
            ))}
          </div>
        )}
      </QueryPage>

      {/* ─── Create/Edit sheet ──────────────────────────────────────────── */}
      {/* urlKey/urlId enable deep-link URL routing (G3 pilot). Only passed
          in edit mode — create mode has no stable id to write to the URL. */}
      <GoalFormDialog
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setCreatedGoalId(null); }}
        onSubmit={handleFormSubmit}
        createdGoalId={createdGoalId}
        editingGoal={editingGoal}
        domains={domains}
        strategyDomainId={strategyDomain?.id ?? (domains[0]?.id ?? '')}
        isLoading={createMutation.isPending || updateMutation.isPending}
        urlKey={editingGoal ? GOAL_URL_KEY : undefined}
        urlId={editingGoal?.id}
      />
    </RorosPage>
  );
}

// ─── Goal Row ──────────────────────────────────────────────────────────────

interface GoalRowProps {
  goal: RorosGoalDTO;
  domains: RorosDomainDTO[];
  onEdit: () => void;
  onArchive: () => void;
}

function GoalRow({ goal, domains, onEdit, onArchive }: GoalRowProps) {
  const domain = domains.find((d) => d.id === goal.domainId);
  const qaPreview = goal.qualitativeAssessment && goal.qualitativeAssessment.length > 80
    ? `${goal.qualitativeAssessment.slice(0, 80)}…`
    : (goal.qualitativeAssessment ?? '');

  return (
    <Card
      className="p-4"
      role="listitem"
      tabIndex={0}
      aria-label={goal.title}
      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onEdit(); }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{goal.title}</h3>
            <Badge variant={statusVariant(goal.status)}>
              {t(goal.status)}
            </Badge>
            <ParentBreadcrumb
              parentType={goal.parentType}
              parentOutcomeId={goal.parentOutcomeId}
              parentOutcomeTitle={goal.parentOutcomeTitle}
            />
          </div>

          {qaPreview && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{qaPreview}</p>
          )}

          <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
            {domain && (
              <span>{t('Domain')}: <span className="font-medium text-foreground">{domain.name}</span></span>
            )}
            {goal.targetDate && (
              <span>{t('Target')}: <span className="font-medium text-foreground">{formatDate(goal.targetDate)}</span></span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            aria-label={t('Edit')}
            tabIndex={0}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {goal.status !== 'archived' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onArchive}
              aria-label={t('Archive')}
              tabIndex={0}
              className="text-destructive hover:text-destructive"
              data-testid="goal-archive-button"
            >
              <Archive className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
